import { UniversalCalculatorConfig } from './UniversalCalculator';
import * as formulas from '../../utils/calculatorFormulas';

export const sipUniversalConfig: UniversalCalculatorConfig = {
  title: 'SIP Calculator',
  subtitle: 'Enter your details to see your SIP projection',
  inputs: [
    {
      key: 'investment',
      label: 'Monthly Investment',
      sliderConfig: { min: 1000, max: 1000000, step: 1000 },
      showClear: true,
      prefix: '₹',
    },
    { key: 'returns', label: 'Expected Annual Return (%p.a.)' },
    {
      key: 'stepUpType',
      label: 'Step-Up Type',
      type: 'select',
      options: [
        { value: 0, label: 'None' },
        { value: 1, label: 'Percentage' },
        { value: 2, label: 'Amount' },
      ],
    },
    { key: 'stepUpValue', label: 'Step-Up Value', hideWhen: (v) => v.stepUpType === 0 },
    {
      key: 'stepUpFrequency',
      label: 'Step-Up Frequency',
      type: 'select',
      options: [
        { value: 6, label: 'Every 6 Months' },
        { value: 12, label: 'Every 12 Months' },
      ],
      hideWhen: (v) => v.stepUpType === 0,
    },
  ],
  useTimePeriod: true,
  defaultValues: {
    investment: 50000,
    returns: 10,
    years: 5,
    months: 0,
    stepUpType: 0,
    stepUpValue: 0,
    stepUpFrequency: 12,
  },
  resultLabel: 'Final Amount',
  stats: [
    { label: 'Total Investment', getValue: (v) => v.investment * (v.years * 12 + v.months) },
    {
      label: 'Wealth Gained',
      getValue: (v, r) => r - v.investment * (v.years * 12 + v.months),
      isWealth: true,
    },
  ],
  chartDatasets: [
    { label: 'Amount Invested', color: '#2B3B5C' },
    { label: 'Wealth Gained', color: '#4CAF50' },
  ],
  calculate: (v) => {
    const stepUpTypeStr = v.stepUpType === 1 ? 'percentage' : v.stepUpType === 2 ? 'amount' : 'none';
    return formulas.calculateSIP(
      v.investment,
      v.returns,
      v.years * 12 + v.months,
      stepUpTypeStr,
      v.stepUpValue,
      v.stepUpFrequency
    );
  },
  generateChartData: (v) => {
    const monthlyRate = v.returns / (12 * 100);
    const totalMonths = v.years * 12 + v.months;
    const totalYears = Math.ceil(totalMonths / 12);
    const step = totalYears <= 5 ? 1 : totalYears <= 10 ? 2 : 5;
    const amountInvested = [];
    const wealthGained = [];
    const stepUpTypeStr = v.stepUpType === 1 ? 'percentage' : v.stepUpType === 2 ? 'amount' : 'none';

    for (let year = 0; year <= totalYears; year += step) {
      const months = year * 12;
      const invested = months <= totalMonths ? v.investment * months : v.investment * totalMonths;
      amountInvested.push(invested);

      if (months === 0) {
        wealthGained.push(0);
      } else if (months <= totalMonths) {
        const totalValue = formulas.calculateSIP(
          v.investment,
          v.returns,
          months,
          stepUpTypeStr,
          v.stepUpValue,
          v.stepUpFrequency
        );
        wealthGained.push(totalValue - invested);
      } else {
        const finalAmount = formulas.calculateSIP(
          v.investment,
          v.returns,
          totalMonths,
          stepUpTypeStr,
          v.stepUpValue,
          v.stepUpFrequency
        );
        const projectedValue = finalAmount * Math.pow(1 + monthlyRate, months - totalMonths);
        wealthGained.push(projectedValue - invested);
      }
    }

    return [amountInvested, wealthGained];
  },
};

export const lumpsumUniversalConfig: UniversalCalculatorConfig = {
  title: 'Lumpsum Calculator',
  subtitle: 'Enter your details to see your Lumpsum projection',
  inputs: [
    {
      key: 'investment',
      label: 'Investment Amount',
      sliderConfig: { min: 10000, max: 10000000, step: 10000 },
      showClear: true,
      prefix: '₹',
    },
    { key: 'returns', label: 'Expected Annual Return (%p.a.)' },
  ],
  useTimePeriod: true,
  defaultValues: { investment: 500000, returns: 10, years: 5, months: 0 },
  resultLabel: 'Final Amount',
  stats: [
    { label: 'Total Investment', getValue: (v) => v.investment },
    { label: 'Wealth Gained', getValue: (v, r) => r - v.investment, isWealth: true },
  ],
  chartDatasets: [
    { label: 'Amount Invested', color: '#2B3B5C' },
    { label: 'Wealth Gained', color: '#4CAF50' },
  ],
  calculate: (v) => formulas.calculateLumpsum(v.investment, v.returns, v.years * 12 + v.months),
  generateChartData: (v) => {
    const totalYears = Math.ceil((v.years * 12 + v.months) / 12);
    const step = totalYears <= 5 ? 1 : totalYears <= 10 ? 2 : 5;
    const amountInvested = [];
    const wealthGained = [];

    for (let year = 0; year <= totalYears; year += step) {
      amountInvested.push(v.investment);
      const totalValue = formulas.calculateLumpsum(v.investment, v.returns, year * 12);
      wealthGained.push(totalValue - v.investment);
    }

    return [amountInvested, wealthGained];
  },
};

export const swpUniversalConfig: UniversalCalculatorConfig = {
  title: 'SWP Calculator',
  subtitle: 'Enter your details to see your SWP projection',
  inputs: [
    {
      key: 'initialInvestment',
      label: 'Initial Investment',
      sliderConfig: { min: 100000, max: 10000000, step: 50000 },
      prefix: '₹',
      showClear: true,
    },
    {
      key: 'withdrawalAmount',
      label: 'Monthly Withdrawal',
      sliderConfig: { min: 1000, max: 100000, step: 1000 },
      prefix: '₹',
      showClear: true,
    },
    { key: 'returns', label: 'Expected Annual Return (%)' },
  ],
  useTimePeriod: true,
  defaultValues: { initialInvestment: 1000000, withdrawalAmount: 10000, returns: 10, years: 10, months: 0 },
  resultLabel: 'Remaining Amount',
  stats: [
    { label: 'Initial Investment', getValue: (v) => v.initialInvestment },
    { label: 'Total Withdrawn', getValue: (v) => v.withdrawalAmount * (v.years * 12 + v.months), isWealth: true },
  ],
  chartDatasets: [
    { label: 'Remaining Investment', color: '#1E2A47' },
    { label: 'Total Withdrawn', color: '#4CAF50' },
  ],
  calculate: (v) => formulas.calculateSWP(v.initialInvestment, v.withdrawalAmount, v.returns, v.years * 12 + v.months),
  generateChartData: (v) => {
    const monthlyRate = v.returns / (12 * 100);
    const totalMonths = v.years * 12 + v.months;
    const investmentWorth = [];
    const totalWithdrawn = [];

    for (let year = 0; year <= 40; year += 5) {
      const months = year * 12;
      let amount = v.initialInvestment;
      let withdrawn = 0;

      for (let i = 0; i < months && i < totalMonths; i++) {
        amount = amount * (1 + monthlyRate) - v.withdrawalAmount;
        withdrawn += v.withdrawalAmount;
        if (amount < 0) {
          amount = 0;
          break;
        }
      }

      investmentWorth.push(Math.max(0, amount));
      totalWithdrawn.push(withdrawn);
    }

    return [investmentWorth, totalWithdrawn];
  },
};

export const inflationUniversalConfig: UniversalCalculatorConfig = {
  title: 'Inflation Calculator',
  subtitle: "See how inflation affects your money's purchasing power over time",
  inputs: [
    {
      key: 'initialAmount',
      label: 'Current Amount',
      sliderConfig: { min: 10000, max: 1000000, step: 10000 },
      prefix: '₹',
      showClear: true,
    },
    { key: 'inflationRate', label: 'Inflation Rate (%)' },
  ],
  useTimePeriod: true,
  defaultValues: { initialAmount: 100000, inflationRate: 6, years: 10, months: 0 },
  resultLabel: 'Future Value',
  stats: [
    { label: 'Current Value', getValue: (v) => v.initialAmount },
    {
      label: 'Inflation Impact',
      getValue: (v, r) => r - v.initialAmount,
      isWealth: true,
    },
  ],
  chartDatasets: [
    { label: 'Inflated Value', color: '#1E2A47' },
    { label: 'Original Value', color: '#4CAF50' },
  ],
  calculate: (v) => formulas.calculateInflation(v.initialAmount, v.inflationRate, v.years + v.months / 12),
  generateChartData: (v) => {
    const inflatedValues = [];
    const originalValues = [];

    for (let year = 0; year <= 40; year += 5) {
      inflatedValues.push(formulas.calculateInflation(v.initialAmount, v.inflationRate, year));
      originalValues.push(v.initialAmount);
    }

    return [inflatedValues, originalValues];
  },
};

export const retirementUniversalConfig: UniversalCalculatorConfig = {
  title: 'Retirement Calculator',
  subtitle: 'Plan your retirement corpus based on your current expenses and goals',
  inputs: [
    {
      key: 'monthlyExpense',
      label: 'Present Monthly Expense',
      sliderConfig: { min: 10000, max: 200000, step: 5000 },
      prefix: '₹',
      showClear: true,
    },
    { key: 'inflationRate', label: 'Inflation Rate (%)' },
    { key: 'currentAge', label: 'Current Age' },
    { key: 'retirementAge', label: 'Retirement Age' },
    { key: 'lifeExpectancy', label: 'Life Expectancy' },
    { key: 'postRetirementReturn', label: 'Expected Return During Retirement (%)' },
    { key: 'preRetirementReturn', label: 'Pre-Retirement Investment Return (%)' },
    {
      key: 'currentCorpus',
      label: 'Current Corpus/Savings',
      sliderConfig: { min: 0, max: 10000000, step: 50000 },
      prefix: '₹',
      showClear: true,
    },
  ],
  useTimePeriod: false,
  defaultValues: {
    monthlyExpense: 50000,
    inflationRate: 6,
    currentAge: 30,
    retirementAge: 60,
    lifeExpectancy: 85,
    postRetirementReturn: 8,
    preRetirementReturn: 12,
    currentCorpus: 500000,
  },
  resultLabel: 'Required Retirement Corpus',
  stats: [
    {
      label: 'Years to Retirement',
      getValue: (v) => v.retirementAge - v.currentAge,
      format: (val) => `${val} years`,
    },
    {
      label: 'Current Corpus Growth',
      getValue: (v) => v.currentCorpus * Math.pow(1 + v.preRetirementReturn / 100, v.retirementAge - v.currentAge),
    },
    {
      label: 'Additional Corpus Needed',
      getValue: (v, r) =>
        Math.max(0, r - v.currentCorpus * Math.pow(1 + v.preRetirementReturn / 100, v.retirementAge - v.currentAge)),
      isWealth: true,
    },
  ],
  chartDatasets: [
    { label: 'Corpus Growth', color: '#1E2A47' },
    { label: 'Annual Expenses', color: '#4CAF50' },
  ],
  calculate: (v) =>
    formulas.calculateRetirementCorpus(
      v.monthlyExpense,
      v.inflationRate,
      v.retirementAge - v.currentAge,
      v.postRetirementReturn,
      v.lifeExpectancy - v.retirementAge
    ),
  generateChartData: (v) => {
    const yearsToRetirement = v.retirementAge - v.currentAge;
    const corpusGrowth = [];
    const expenseGrowth = [];

    for (let year = 0; year <= yearsToRetirement; year += Math.max(1, Math.floor(yearsToRetirement / 8))) {
      corpusGrowth.push(v.currentCorpus * Math.pow(1 + v.preRetirementReturn / 100, year));
      expenseGrowth.push(v.monthlyExpense * 12 * Math.pow(1 + v.inflationRate / 100, year));
    }

    return [corpusGrowth, expenseGrowth];
  },
};
